import { validateAndSanitiseGBPostcode } from "@./address/validation";

import {
  IAddress,
  TPostcodeCheckResponse,
  AddressValidationState,
  IAddressValidationResult,
  IUserAddress,
  TPostcoderResponse,
} from "./address.types";
import {
  combineAddressLines,
  findBestMatch,
  formatAddress,
  sanitizeAddress,
  sanitizeGBPostcode,
} from "./address.utils";
import { ADDRESS_SIMILARITY_THRESHOLD } from "./address.constants";

const NOT_FOUND_STATUS = 404;
// This is not a secret key
const POSTCODER_KEY = "PCW8V-K6BEC-46YEU-Y392R";
const POSTCODER_COUNTRY = "GB";

export function Address(): IAddress {

  const postcodeCheck = (
    postcode: string,
    skipOutOfBounds = false,
  ): TPostcodeCheckResponse => validateAndSanitiseGBPostcode(postcode, skipOutOfBounds);

  const fetchAddressFromPostcode = async (postcode: string): Promise<TPostcoderResponse> => {
    const sanitizedPostcode = sanitizeGBPostcode(postcode);
    const url = `https://ws.postcoder.com/pcw/${POSTCODER_KEY}/address/${POSTCODER_COUNTRY}/${sanitizedPostcode}?lines=2`;
    const response = await fetch(url, {
      method: "GET",
    });

    if (response.ok) {
      return response.json();
    }
    if (response.status === NOT_FOUND_STATUS) {
      return [];
    }
    const body = await response.text();
    throw new Error(`Something went wrong: ${response.status}: ${body}`);
  };

  const checkAddressIsValid = async (userAddress: IUserAddress): Promise<IAddressValidationResult> => {
    try {
      const addresses = await fetchAddressFromPostcode(userAddress.postcode);

      const formattedAddresses = addresses.map((address) => formatAddress(address, userAddress.postcode));

      const combinedUserAddress = [
        userAddress.address1,
        userAddress.address2,
        userAddress.city,
      ].filter(Boolean).join(" ");

      const sanitizedUserAddress = sanitizeAddress(combinedUserAddress);
      const sanitizedUserCity = sanitizeAddress(userAddress.city);

      const exactMatch = formattedAddresses.some((address) => {
        const sanitizedApiAddress = sanitizeAddress(combineAddressLines(address));
        const sanitizedApiCity = sanitizeAddress(address.city);

        return (sanitizedUserAddress === sanitizedApiAddress) && (sanitizedUserCity === sanitizedApiCity);
      });

      if (exactMatch) {
        return { state: AddressValidationState.VALID };
      }

      const { address: bestMatch, similarity } = findBestMatch(sanitizedUserAddress, formattedAddresses);

      if (bestMatch && similarity > ADDRESS_SIMILARITY_THRESHOLD) {
        return {
          state: AddressValidationState.SIMILAR,
          suggestedAddress: bestMatch,
        };
      }

      return {
        state: AddressValidationState.NO_MATCH,
      };

    } catch {
      return {
        state: AddressValidationState.ERROR,
      };
    }
  };

  return {
    fetchAddressFromPostcode,
    postcodeCheck,
    checkAddressIsValid,
  };
}

export default Address;
